import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`forms-tabpanel-${index}`}
      aria-labelledby={`forms-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2, ...viewRootTextPrimarySx }}>{children}</Box>}
    </div>
  );
}

// Link Button component. `href` opens an external page in a new tab, `to` navigates
// to an internal route via react-router.
function LinkButton({
  href,
  to,
  children,
  color = 'primary',
  variant = 'contained',
  startIcon,
  ...props
}) {
  const linkProps = to
    ? { component: RouterLink, to }
    : { href, target: '_blank', rel: 'noopener noreferrer' };
  return (
    <Button
      {...linkProps}
      variant={variant}
      color={color}
      size='medium'
      startIcon={startIcon || (to ? undefined : <OpenInNew />)}
      sx={{
        mb: 1.5,
        mr: 1.5,
        minWidth: { xs: '100%', sm: '200px' },
        width: { xs: '100%', sm: 'auto' },
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

// Section component
function Section({ title, children }) {
  return (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent>
        <Typography variant='h6' component='h3' sx={{ mb: 1.5, fontWeight: 600, fontSize: '1rem' }}>
          {title}
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            '& > *': {
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
            },
          }}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function FormsAndLinks() {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth='lg' sx={{ py: 2, ...viewRootTextPrimarySx }}>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant='h5' component='h1' sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
          Formulare & Links
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='forms and links tabs'
            sx={{
              '& .MuiTab-root': {
                fontSize: '0.9rem',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: '50px',
              },
            }}
          >
            <Tab label='Aushilfen' />
            <Tab label='Standortleitung' />
          </Tabs>
        </Box>

        <Alert severity='warning' sx={{ mt: 1, mb: 2 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
            Hinweis IT-Support Hotline
          </Typography>
          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
            Nummer des IT-Supports: <strong>0211-923244-99</strong>
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant='caption' sx={{ display: 'block', fontSize: '0.75rem' }}>
              Mo-Fr: 09:00-17:30
            </Typography>
            <Typography variant='caption' sx={{ display: 'block', fontSize: '0.75rem' }}>
              Notfallhotline bei Betriebsstörung: Mo-So: Ganztägig
            </Typography>
          </Box>
        </Alert>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Section title='Firmenanfragen'>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/64QOhtEIn45S45r7BUUOPsGW2DzE2ISGWGJGc7YAT7soXcZFENH4pkdZrCxyXVDWzp'
                  color='warning'
                >
                  LaserZone
                </LinkButton>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/63gURjj4FRfSouI3QqHwv7FjayZ9ZbiIhVBezaRwe0OsEUpvVLaqIcmKpj2J9hgkPp'
                  color='warning'
                >
                  GlowZone
                </LinkButton>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/72mr6FNAilQubGlgt2n3jiCIBTx5fKy1T0EY5ftrBeH6ZnsYdri2mWy5yyuwtthx19'
                  color='warning'
                >
                  ExitZone
                </LinkButton>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/ckqnkrQtBp4cxwYPKN12ETWHAPvwkWPSrklCsq0Kxm82Rqbd8688pxAGLvvc1GUuMr'
                  color='warning'
                >
                  BashZone
                </LinkButton>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/bXCYvWqCCJdoSRKZEZSOecGC0V7p9XOr6ZrCqsPy6SXTVXvjCphkwv2vKpakwoRCBd'
                  color='warning'
                >
                  PaintballZone
                </LinkButton>
                <LinkButton
                  href='https://axezone.de/axtwerfen-gruppen-angebote/firmenevents/#inquiry-form'
                  color='warning'
                >
                  AxeZone
                </LinkButton>
                <LinkButton
                  href='https://webforms.pipedrive.com/f/63tPrnG6yfC9IEJzdJLJKGZgY4kMP872up7Q3OU2fnSwug6Fa0uWpCIZ6bGxCUT4JR'
                  color='warning'
                >
                  RallyeZone
                </LinkButton>
                <LinkButton
                  href='https://karaokezone.de/gruppen-angebote/firmenfeier/#inquiry-form'
                  color='warning'
                >
                  KaraokeZone
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section title='Meldungen / interne Formulare'>
                <LinkButton href='https://forms.office.com/r/tpjmj3s6pd' color='success'>
                  Meldung an Standortleitung
                </LinkButton>
                <LinkButton to='/verbandbuch' color='warning'>
                  Verbandbuch
                </LinkButton>
                <LinkButton href='https://whistleblower.fun-zone.de' color='error'>
                  Whistleblower-Formular
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Alert severity='error' sx={{ mt: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                  Hinweis Whistleblower-Formular
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  Falls du das Formular aus Gründen der Anonymität zu Hause ausfüllen möchtest,
                  präge dir bitte folgenden Link ein oder schreibe ihn dir auf:
                </Typography>
                <Box sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    https://whistleblower.fun-zone.de
                  </Typography>
                </Box>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Section title='Interne Meldungen an Fachabteilungen'>
                <LinkButton
                  href='https://fun-zone.atlassian.net/servicedesk/customer/portal/1'
                  color='error'
                >
                  Meldung an IT-Support
                </LinkButton>
                <LinkButton href='https://forms.office.com/e/cca59f0yrX' color='warning'>
                  Meldung an CC-Leitung
                </LinkButton>
                <LinkButton href='https://whistleblower.fun-zone.de' color='error'>
                  Whistleblower-Formular
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section title='Nachbestellungen'>
                <LinkButton
                  href='https://forms.monday.com/forms/619ce550422417fd583d3e9eacff006d?r=use1'
                  color='success'
                >
                  Nachbestellung Marketing
                </LinkButton>
                <LinkButton href='https://forms.office.com/r/40XDcsVrrs' color='success'>
                  Nachbestellung Kartendruckermaterial
                </LinkButton>
                <LinkButton href='https://forms.office.com/e/riFeEdJXSq' color='success'>
                  Nachbestellung Namensschilder (Festang.)
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section title='Berichte und Übersichten'>
                <LinkButton
                  href='https://forms.office.com/Pages/ResponsePage.aspx?id=ncaT7J3xhUKoQxU7YzMVQbDxu1BULMtBn6z2zx8hvoBUNU9MRjVLVjlVUFJWT01BR1QwVFJXQ1laNC4u'
                  color='info'
                >
                  Wochenbericht Standortleitung
                </LinkButton>
                <LinkButton
                  href='https://dashthis.com/app/view/dashboard-5SDaGCrJeEqfty_XyXKMdg?period=1'
                  color='warning'
                >
                  Bewertungsübersicht anzeigen
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section title='Links für MA'>
                <LinkButton href='https://anmeldung-ma.fun-zone.de' color='primary'>
                  Neuen Mitarbeiter anmelden (zum weiterleiten)
                </LinkButton>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Alert severity='info' sx={{ mt: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                  Hinweis Anmeldung neue MA
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  Hier der Link zur Weiterleitung im Klartext:
                </Typography>
                <Box sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography
                    variant='body2'
                    component='a'
                    href='https://anmeldung-ma.fun-zone.de'
                    target='_blank'
                    rel='noopener noreferrer'
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    https://anmeldung-ma.fun-zone.de
                  </Typography>
                </Box>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Alert severity='error' sx={{ mt: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                  Hinweis Whistleblower-Formular
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  Falls du das Formular aus Gründen der Anonymität zu Hause ausfüllen möchtest,
                  präge dir bitte folgenden Link ein oder schreibe ihn dir auf:
                </Typography>
                <Box sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}
                  >
                    https://whistleblower.fun-zone.de
                  </Typography>
                </Box>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
}
